import React, { useContext, useEffect, useState } from 'react';
import { 
  AppBar, Toolbar, Typography, Button, 
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions 
} from '@mui/material';
import { Link, useNavigate, useLocation, useMatch } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AuthContext } from './AuthContextProvider';
import * as Constants from './Constants';

const MENU_CONFIG = {
  [Constants.ROLE_SITE_ADMIN]: [
    { label: 'Admins', path: '/siteAdmins' },
    { label: 'Sites', path: '/sites' },
    { label: 'Profile', path: '/updateUser' },
    { label: 'Schedule', path: '/siteEvents' },
    { label: 'Usage', path: '/globalUsage' },
  ],
  [Constants.ROLE_COMMUNITY_ADMIN]: [
    { label: 'Today', path: '/today' },
    { label: 'Schedule', path: '/scheduledEvents' },
    { label: 'Classes', path: '/classes' },
    { label: 'MOTD', path: '/motds' },
    { label: 'Event', path: '/events' },
    { label: 'User', path: '/users' },
    { label: 'Group', path: '/groups' },
    { label: 'Photo', path: '/photos' },
    { label: 'Usage', path: '/usage' },
  ],
  [Constants.ROLE_FAMILY_MEMBER]: [
    { label: 'Zoom', path: '/zoom' },
    { label: 'Photo', path: '/photos' },
    { label: 'Profile', path: '/updateUser' },
  ],
  [Constants.ROLE_PROGRAM_DIRECTOR]: [
    { label: 'Today', path: '/today' },
    { label: 'Schedule', path: '/scheduledEvents' },
    { label: 'Classes', path: '/classes' },
    { label: 'Event', path: '/events' },
  ],
  [Constants.ROLE_INSTRUCTOR]: [
    { label: 'Today', path: '/today' },
  ],
};

const HeaderAppBar = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const isBingoPage = useMatch('/bingoEvent/:id');
  const isVideoPage = useMatch('/videoEvent/:id');
  const isRestrictedEvent = Boolean(isBingoPage || isVideoPage);

  useEffect(() => {
    if (user?._site?.name) {
      document.title = user._site.name;
    } else {
      document.title = 'TeleVisit Portal';
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleConfirmLeave = () => {
    setConfirmOpen(false);
    navigate('/today');
  };

  // Restricted Event Header
  if (isRestrictedEvent) {
    return (
      <>
        <AppBar className="AppBar" position="static">
          <Toolbar>
            <Button
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={() => setConfirmOpen(true)}
            >
              Leave Event
            </Button>
            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center' }}>
              {user?._site?.name ? `Site: ${user._site.name}` : 'TeleVisit Portal'}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Leave Confirmation Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Leave Event?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to leave this event? If you have an active TELECON, this will cause problems
              for other participants. Please make sure to end the TELECON before leaving.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleConfirmLeave} color="error" variant="contained" autoFocus>
              Leave
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Standard Header logic continues below...
  const currentRoleItems = user?.loginRole ? MENU_CONFIG[user.loginRole] || [] : [];

  return (
    <AppBar className="AppBar" position="static">
      <Toolbar>
        <Typography
          variant={user?.loginRole === Constants.ROLE_FAMILY_MEMBER ? 'body1' : 'h6'}
          component={Link}
          to="/home"
          sx={{
            flexGrow: 1,
            color: 'inherit',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
        >
          {user?.loginRole === Constants.ROLE_FAMILY_MEMBER
            ? 'HOME'
            : `TeleVisit Portal (v1.6.4) ${user?._site?.name ? `Site: ${user._site.name}` : ''}`}
        </Typography>

        {currentRoleItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Button
              key={item.path}
              component={Link}
              to={item.path}
              color={isActive ? 'secondary' : 'inherit'}
            >
              {item.label}
            </Button>
          );
        })}

        {user && (
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default HeaderAppBar;